// Task module types — mirror of the ph_server tasks contract (TASKS_CONTRACT §2–§4).

export interface TaskUser {
  id: string;
  name: string;
  role_identifier?: string;
}

export interface TaskProperty {
  id: string;
  name: string;
  address?: string | null;
}

export interface TaskRelated {
  type: RelatedType | string;
  id: string;
  name: string;
}

export interface TaskChecklistSummary {
  done: number;
  total: number;
}

export interface Task {
  id: string;
  task_id: string; // read-only, server-generated 6-char code
  title: string;
  description?: string | null;
  task_type?: TaskType | string | null;
  category?: string | null; // legacy
  priority: TaskPriority | string;
  status: TaskStatus | string;
  hold_reason?: string | null;
  due_date?: string | null; // YYYY-MM-DD
  planned_start?: string | null; // HH:MM
  planned_end?: string | null; // HH:MM
  start_date?: string | null; // legacy
  end_date?: string | null; // legacy
  location_text?: string | null;
  assignee?: TaskUser | null;
  owner?: TaskUser | null; // legacy alias of assignee
  property?: TaskProperty | null;
  related?: TaskRelated | null;
  client_type?: ClientType | string | null;
  outcome?: TaskOutcome | string | null;
  completed_at?: string | null;
  completed_by?: string | null;
  completion_note?: string | null;
  verified_at?: string | null;
  verified_by?: string | null;
  is_overdue?: boolean;
  checklist?: TaskChecklistSummary | null;
  recurrence_id?: string | null;
  created_at: string;
  updated_at?: string | null;
  created_by?: string | null;
  created_by_id?: string | null;
  uploads?: TaskUpload[];
}

export interface TaskUpload {
  id: string;
  upload_key: string;
  original_name: string;
  mime_type: string;
  size?: number;
  kind?: 'attachment' | 'completion_photo' | string;
  created_at?: string;
}

export interface ChecklistItem {
  id: string;
  label: string;
  position: number;
  is_done: boolean;
  done_by?: string | null;
  done_at?: string | null;
}

export interface ActivityLog {
  id: string;
  task?: { id: string; task_id: string; title: string } | null;
  user?: { id: string; name: string } | null;
  activity_type: string;
  started_at: string;
  ended_at?: string | null;
  duration_min?: number | null;
  location_text?: string | null;
  lat?: number | null;
  lng?: number | null;
  accuracy_m?: number | null;
  property?: TaskProperty | null;
  related?: TaskRelated | null;
  client_type?: ClientType | string | null;
  remarks?: string | null;
  source: 'task' | 'manual' | string;
  created_at: string;
}

export interface ActivityLogTotals {
  minutes: number;
  site_visits: number;
  entries: number;
}

export interface ActivityLogListResponse {
  success: boolean;
  message: string;
  data: {
    logs: ActivityLog[];
    total: number;
    pageCount: number;
    totals: ActivityLogTotals;
  };
}

export interface Recurrence {
  id: string;
  title: string;
  description?: string | null;
  task_type: TaskType | string;
  priority?: TaskPriority | string | null;
  property?: TaskProperty | null;
  assignee?: TaskUser | null;
  checklist?: string[];
  frequency: RecurrenceFrequency | string;
  interval: number;
  start_date: string;
  end_date?: string | null;
  due_after_days: number;
  planned_start?: string | null;
  planned_end?: string | null;
  next_run_at?: string | null;
  is_active: boolean;
  last_created_at?: string | null;
  created_count: number;
}

export interface TaskSummary {
  due_today: number;
  overdue: number;
  in_progress: number;
  done_this_week: number;
  open: number;
}

export interface MyDay {
  date: string;
  up_next: Task[];
  overdue: Task[];
  done: Task[];
  activities: ActivityLog[];
}

export interface TaskListResponse {
  success: boolean;
  message: string;
  data: {
    tasks: Task[];
    total: number;
    pageCount: number;
  };
}

export interface TaskDetailResponse {
  success: boolean;
  message: string;
  data: Task;
}

export interface TaskComment {
  id: string;
  comment: string;
  created_at: string;
  created_by: string;
}

export interface TaskCommentsResponse {
  success: boolean;
  message: string;
  data: TaskComment[];
}

export interface TaskHistory {
  id: string;
  field: 'status' | 'priority' | 'category' | 'owner' | 'task_type' | 'assignee' | 'due_date' | string;
  previous_value: string | null;
  new_value: string;
  changed_at: string;
  changed_by: string | null;
}

export interface TaskHistoryResponse {
  success: boolean;
  message: string;
  data: TaskHistory[];
}

export interface TaskFormData {
  title: string;
  description?: string;
  task_type?: TaskType | string;
  category?: string;
  priority?: TaskPriority | string;
  status?: TaskStatus | string;
  due_date?: string;
  planned_start?: string;
  planned_end?: string;
  location_text?: string;
  assignee_id?: string;
  property_id?: string;
  related_type?: RelatedType | string;
  related_id?: string;
  checklist?: string[];
}

/** Pre-filled link used when the task form is opened from a property / lead page. */
export interface TaskLinkPrefill {
  property_id?: string;
  property_name?: string;
  related_type?: RelatedType | string;
  related_id?: string;
  related_name?: string;
}

// ── Enumerations (verbatim from the server) ────────────────────────────────

export type TaskType =
  | 'Site visit'
  | 'Inspection'
  | 'Repair / Renovation follow-up'
  | 'Vendor coordination'
  | 'Tenant / owner meeting'
  | 'Rent collection'
  | 'Document / portal work'
  | 'Sales visit'
  | 'Other';

export const TASK_TYPES: TaskType[] = [
  'Site visit',
  'Inspection',
  'Repair / Renovation follow-up',
  'Vendor coordination',
  'Tenant / owner meeting',
  'Rent collection',
  'Document / portal work',
  'Sales visit',
  'Other',
];

export enum TaskPriority {
  HIGH = 'High',
  MEDIUM = 'Medium',
  LOW = 'Low',
}

export const TASK_PRIORITIES: string[] = Object.values(TaskPriority);

export enum TaskStatus {
  NEW = 'New',
  IN_PROGRESS = 'In Progress',
  ON_HOLD = 'On Hold',
  DONE = 'Done',
  CANCELLED = 'Cancelled',
}

export const TASK_STATUSES: string[] = Object.values(TaskStatus);

/** Lanes shown on the board (Cancelled is intentionally not a lane). */
export const TASK_BOARD_STATUSES: string[] = [
  TaskStatus.NEW,
  TaskStatus.IN_PROGRESS,
  TaskStatus.ON_HOLD,
  TaskStatus.DONE,
];

export type TaskOutcome = 'Done' | 'Partially done' | 'Could not do';
export const TASK_OUTCOMES: TaskOutcome[] = ['Done', 'Partially done', 'Could not do'];

export type RelatedType = 'property' | 'lead' | 'enquiry' | 'service_request' | 'inspection';
export const RELATED_TYPES: RelatedType[] = ['property', 'lead', 'enquiry', 'service_request', 'inspection'];

export type ClientType = 'Existing' | 'Lead';

/** Ad-hoc activity entries pick from the same list as task types. */
export const ACTIVITY_TYPES: string[] = [
  ...TASK_TYPES,
  'New Lead Follow-up',
  'Reached Out',
  'Inspection Done',
  'Introduction Done',
  'Draft Agreement Sent',
  'Final Agreement Sent',
  'Token/Fee Paid',
  'Calls Follow-up',
  'Assign Visits',
  'Adding Property to Portal/Listing Sites',
  'Rental Visits',
];

export type RecurrenceFrequency = 'Daily' | 'Weekly' | 'Monthly' | 'Quarterly' | 'Yearly';
export const RECURRENCE_FREQUENCIES: RecurrenceFrequency[] = ['Daily', 'Weekly', 'Monthly', 'Quarterly', 'Yearly'];

/** Legacy category values — kept so existing rows still render/edit. */
export enum TaskCategory {
  HR = 'HR',
  ACCOUNTS = 'Accounts',
  FINANCE = 'Finance',
  MARKETING = 'Marketing',
  LEAD_MANAGEMENT = 'Lead Management',
  SURVEYS = 'Surveys',
  REVIEWS = 'Reviews',
  WEBSITE = 'Website',
  SALES = 'Sales',
}

// ── Select option lists ───────────────────────────────────────────────────

const toOptions = (values: string[]) => values.map((value) => ({ value, label: value }));

export const TASK_TYPE_OPTIONS = toOptions(TASK_TYPES);
export const TASK_PRIORITY_OPTIONS = toOptions(TASK_PRIORITIES);
export const TASK_STATUS_OPTIONS = toOptions(TASK_STATUSES);
export const TASK_OUTCOME_OPTIONS = toOptions(TASK_OUTCOMES);
export const RECURRENCE_FREQUENCY_OPTIONS = toOptions(RECURRENCE_FREQUENCIES);
export const TASK_CATEGORY_OPTIONS = toOptions(Object.values(TaskCategory));

export const DUE_FILTER_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'today', label: 'Today' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'week', label: 'This week' },
  { value: 'range', label: 'Date range' },
];

// ── Display helpers ───────────────────────────────────────────────────────

export function taskStatusClass(status: string | null | undefined): string {
  switch (status) {
    case TaskStatus.NEW: return 'bg-secondary';
    case TaskStatus.IN_PROGRESS: return 'bg-primary';
    case TaskStatus.ON_HOLD: return 'bg-warning text-dark';
    case TaskStatus.DONE: return 'bg-success';
    case TaskStatus.CANCELLED: return 'bg-dark';
    default: return 'bg-light text-dark';
  }
}

export function taskPriorityClass(priority: string | null | undefined): string {
  switch (priority) {
    case TaskPriority.HIGH: return 'bg-danger';
    case TaskPriority.MEDIUM: return 'bg-warning text-dark';
    case TaskPriority.LOW: return 'bg-info';
    default: return 'bg-light text-dark';
  }
}

/** "First Last" → "FL" for avatar chips. */
export function initials(name: string | null | undefined): string {
  if (!name) return '?';
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join('');
}

/** Whole days a task is past its due date (0 when not overdue). */
export function overdueDays(task: Pick<Task, 'due_date' | 'is_overdue'>): number {
  if (!task.is_overdue || !task.due_date) return 0;
  const due = new Date(task.due_date + 'T00:00:00');
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.max(1, Math.round((today.getTime() - due.getTime()) / 86400000));
}

/** Human label of the linked record type ("service_request" → "Service request"). */
export function relatedTypeLabel(type: string | null | undefined): string {
  if (!type) return '';
  const s = type.replace(/_/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Local YYYY-MM-DD for date inputs. */
export function toDateInput(d: Date): string {
  const m = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}
